import React, { FC } from 'react';
import { Formik } from 'formik';
import { Form, FormItem, Input } from 'formik-antd';
import { object } from 'yup';
import { FormikOnSubmit } from 'lib/formik.utils';
import {
  requiredEmailValidationSchema,
  firstNameValidationSchema,
  lastNameValidationSchema,
  phoneNumberValidationSchema,
  phoneNumberPrefixCodeValidation,
  jobTitleValidationSchema,
} from 'common/config/validationSchema';
import { defaultFormLayout } from 'common/config/formLayout';
import { MedicalOperatorFormModel } from 'models/medicalOperator';
import { AdministrationGuard } from 'common/components/AdministrationGuard';
import { useRole } from 'common/hooks/useRole';
import { SaveButton } from 'common/components/SaveButton';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { SubpartnersRegisterSelect } from 'common/components/SubpartnersSelect';
import { Checkbox } from 'antd';
import { FormattedMessage } from 'react-intl';
import { isNil } from 'ramda';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';

const emptyInitialValues: MedicalOperatorFormModel = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  prefixCode: '',
  jobTitle: '',
  partner: '',
};

export interface BusinessPartnerRegisterMedicalOperatorFormProps {
  onSubmit: FormikOnSubmit<MedicalOperatorFormModel>;
  loading: boolean;
  initialValues?: MedicalOperatorFormModel;
  setCheckedDenyAccessToApps?: React.Dispatch<React.SetStateAction<boolean>>;
  setCheckedDenyAccessToPatients: React.Dispatch<React.SetStateAction<boolean>>;
  setCheckedDenyAccessToHigoProOne: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

export const BusinessPartnerMedicalOperatorForm: FC<BusinessPartnerRegisterMedicalOperatorFormProps> = ({
  onSubmit,
  loading = false,
  initialValues,
  setCheckedDenyAccessToApps,
  setCheckedDenyAccessToPatients,
  setCheckedDenyAccessToHigoProOne,
}) => {
  const { isAdministrationRole } = useRole();
  const intlMessage = useFormatMessage();

  const validationSchema = object().shape(
    {
      firstName: firstNameValidationSchema(intlMessage),
      lastName: lastNameValidationSchema(intlMessage),
      email: requiredEmailValidationSchema(intlMessage),
      phoneNumber: phoneNumberValidationSchema(intlMessage),
      prefixCode: phoneNumberPrefixCodeValidation(intlMessage),
      jobTitle: jobTitleValidationSchema(intlMessage),
    },
    [
      ['prefixCode', 'phoneNumber'],
      ['phoneNumber', 'prefixCode'],
    ]
  );

  const { isMedicalOperator } = useRole();
  const bpContext = useEnsureContext(BusinessPartnerContext);

  const shouldHidePartnerDropdown =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues || emptyInitialValues}
      onSubmit={onSubmit}
    >
      <fieldset disabled={!isAdministrationRole}>
        <Form {...defaultFormLayout}>
          <FormItem
            label={intlMessage({ id: 'person.firstName' })}
            name="firstName"
            required
          >
            <Input name="firstName" />
          </FormItem>
          <FormItem
            label={intlMessage({ id: 'person.lastName' })}
            name="lastName"
            required
          >
            <Input name="lastName" />
          </FormItem>
          <FormItem label={intlMessage({ id: 'email' })} name="email" required>
            <Input name="email" />
          </FormItem>{' '}
          <FormItem
            name="phone"
            label={intlMessage({ id: 'person.phoneNumber' })}
            style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
          >
            <FormItem
              name="phoneNumber"
              style={{ marginBottom: '0', display: 'flex', width: '100%' }}
            >
              <FormItem
                name="prefixCode"
                style={{
                  marginBottom: '0',
                  width: 95,
                  display: 'inline-block',
                }}
              >
                <Input
                  name="prefixCode"
                  style={{ width: 95 }}
                  addonBefore={'+'}
                />
              </FormItem>
              <Input
                style={{ width: 240, marginLeft: 10 }}
                name="phoneNumber"
              />
            </FormItem>
          </FormItem>
          <FormItem
            label={intlMessage({ id: 'person.jobTitle' })}
            name="jobTitle"
          >
            <Input name="jobTitle" />
          </FormItem>
          {!shouldHidePartnerDropdown && (
            <FormItem label={intlMessage({ id: 'partner' })} name="partner">
              <SubpartnersRegisterSelect name="partner" />
            </FormItem>
          )}
          <FormItem
            name="denyAccessToApps"
            style={{ marginLeft: '200px', width: '100%' }}
          >
            <Checkbox
              id="checkbox-deny-access-to-doctor-portal-and-admin-panel"
              key="denyAccess"
              onChange={(e) => {
                setCheckedDenyAccessToApps &&
                  e.target.checked &&
                  setCheckedDenyAccessToApps(true);
              }}
            />
            <span style={{ marginLeft: '7px' }}>
              {' '}
              <FormattedMessage id="medicalOperators.denyAccessToApps" />
            </span>
          </FormItem>
          <FormItem
            name="denyAccessToPatients"
            style={{ marginLeft: '200px', width: '100%' }}
          >
            <Checkbox
              id="checkbox-deny-access-to-creating-and-editing-patients"
              key="denyAccess"
              onChange={(e) => {
                setCheckedDenyAccessToPatients &&
                  e.target.checked &&
                  setCheckedDenyAccessToPatients(true);
              }}
            />
            <span style={{ marginLeft: '7px' }}>
              {' '}
              {/* <FormattedMessage id="medicalOperators.denyAccessToPatiens" />  - add this key to the translation file*/}
              Deny access to creating and editing patients
            </span>
          </FormItem>
          <FormItem
            name="denyAccess"
            style={{ marginLeft: '200px', width: '100%' }}
          >
            <Checkbox
              id="checkbox-higo-pro-one"
              key="denyAccess"
              onChange={(e) => {
                setCheckedDenyAccessToHigoProOne &&
                  e.target.checked &&
                  setCheckedDenyAccessToHigoProOne(true);
              }}
            />
            <span style={{ marginLeft: '7px' }}>
              {' '}
              {/* <FormattedMessage id="medicalOperators.denyAccessToPatiens" />  - add this key to the translation file*/}
              Higo PRO One
            </span>
          </FormItem>
          <AdministrationGuard>
            <SaveButton id="button-save-medical-operator" loading={loading} />
          </AdministrationGuard>
        </Form>
      </fieldset>
    </Formik>
  );
};
